import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { SESSION_LENGTH_CHOICES_MIN, THRESHOLDS } from '@erectus/shared';
import type { GateReason, SessionLengthMin } from '@erectus/shared';
import { Button, Pill, Toggle } from '@/ui/primitives.js';
import { BottomSheet, COLLAPSED_SHEET_CSS } from '@/ui/BottomSheet.js';
import { SETUP_CHECKS, checkPassed } from '@/pose/qualityGate.js';
import { appConfig } from '@/lib/remoteConfig.js';
import { deviceClass, effectiveFps, effectivePoseModel } from '@/lib/device.js';
import { track } from '@/lib/analytics.js';
import { setCuesEnabled } from '@/lib/cues.js';
import { useAuth } from '@/stores/auth.js';
import { newSessionId, rememberFacing, useSession } from '@/stores/session.js';
import { useCapture } from './useCapture.js';
import { useAutoStart } from './useAutoStart.js';
import { SkeletonOverlay } from './SkeletonOverlay.js';
import { CameraFlipButton } from './CameraFlipButton.js';
import { GateChecklist } from './GateChecklist.js';
import { ArmedIndicator } from './ArmedIndicator.js';
import { CameraLoading } from './CameraLoading.js';
import { phoneRow, useTilt } from './useTilt.js';
import { Icon } from '@/ui/icons.js';
import { cameraDistance } from '@/lib/units.js';

/**
 * Sheet heights as fractions of the viewport: handle only, peek, expanded.
 * The expanded height deliberately stops at 72% so the gate checklist above it
 * still fits — the checklist is what you watch while positioning the phone, and
 * the sheet holds settings you touch once.
 */
const SHEET_SNAPS: [number, number, number] = [0.09, 0.24, 0.72];

/**
 * Capture Setup (docs/PRODUCT.md §2 screen 4).
 *
 * Full-screen portrait preview with a draggable sheet over it. The layout is
 * driven by how this is actually used: the phone stands on a shelf and the
 * person is two metres away, so the picture needs to be as large as possible
 * and the controls need to be reachable with one thumb before they walk off.
 *
 * "Start when I sit down" is the primary action. Everything about it — the
 * hold, the sound cues, keeping the same camera — exists because the user is
 * not at the phone when the session begins.
 */
export function CaptureSetupPage(): ReactNode {
  const navigate = useNavigate();
  const { user, userDoc } = useAuth();
  const session = useSession();
  const { plannedMin, setPlanned, begin, configure } = session;
  const config = appConfig();
  const cls = deviceClass();
  // One rate for everyone (3 fps, Remote Config can change it centrally), so
  // sessions stay comparable; no per-user choice. Long sessions and low-end
  // phones still drop to 2 fps on their own.
  const sampleFps = effectiveFps(config.sampleFps, cls);
  const poseModel = effectivePoseModel(config.poseModel, cls);

  const sessionIdRef = useRef(newSessionId());
  const capture = useCapture({
    sessionId: sessionIdRef.current,
    ...(user ? { uid: user.uid } : {}),
    sampleFps,
    poseModel,
    calibration: userDoc?.calibration ?? null,
    facing: session.facing,
    onFacingChange: (facing) => {
      configure({ facing });
      rememberFacing(facing);
    },
  });

  const [sheetExpanded, setSheetExpanded] = useState(false);
  // Opens with the sheet low, showing just the two start buttons; settings are
  // one drag up. Tapping "Start when I sit down" drops it to its handle.
  const [sheetSnap, setSheetSnap] = useState<0 | 1 | 2>(1);
  const reportedChecks = useRef(new Set<string>());

  const gateReasons = capture.tick?.gate.reasons;
  // Phone roll: angles are read against the picture, so a tilted phone tilts
  // every angle. Left out where the device has no motion sensor.
  const { tilt, enable: enableTilt } = useTilt();
  const checks = useMemo(() => {
    const reasons = gateReasons ?? (['noPose'] as GateReason[]);
    const rows: Array<{ id: (typeof SETUP_CHECKS)[number]['id'] | 'phone_upright'; label: string; passed: boolean }> =
      SETUP_CHECKS.map((check) => ({ id: check.id, label: check.label, passed: checkPassed(check, reasons) }));
    if (tilt !== null) {
      rows.splice(1, 0, phoneRow(tilt));
    }
    return rows;
  }, [gateReasons, tilt]);
  const allPassed = capture.modelReady && checks.every((c) => c.passed);

  useEffect(() => {
    for (const check of checks) {
      if (reportedChecks.current.has(check.id) || !capture.tick) continue;
      reportedChecks.current.add(check.id);
      track('capture_setup_check', { check: check.id, passed: check.passed });
    }
  }, [checks, capture.tick]);

  const launch = (): void => {
    enableTilt();
    configure({ sampleFps, poseModel, calibration: userDoc?.calibration ?? null });
    begin(sessionIdRef.current);
    void capture.stop();
    navigate('/session');
  };

  const autoStart = useAutoStart({ conditionsMet: allPassed, onStart: launch });

  // Arming is always a tap — that tap is also what lets iOS play the chimes.
  const { arm } = autoStart;
  const armByHand = (): void => {
    enableTilt();
    setSheetSnap(0);
    setSheetExpanded(false);
    void arm();
  };
  const cancelAutoStart = (): void => {
    autoStart.disarm();
    setSheetSnap(1);
  };

  // Keep the screen awake while armed: the user has walked away from the phone.
  useEffect(() => {
    if (!autoStart.armed) return;
    let lock: { release(): Promise<void> } | null = null;
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
    };
    void nav.wakeLock
      ?.request('screen')
      .then((l) => {
        lock = l;
      })
      .catch(() => null);
    return () => {
      void lock?.release().catch(() => null);
    };
  }, [autoStart.armed]);


  // Errors on this screen count too, not only during a session (category only).
  useEffect(() => {
    if (capture.error) track('session_error', { code: capture.error.code as 'model_load' });
  }, [capture.error]);

  const tick = capture.tick;
  const failing = checks.filter((c) => !c.passed);
  // Sit the checklist just above whatever height the sheet is currently at.
  // Mirrors BottomSheet's own height (snap fraction + bottom safe area);
  // without the inset the hint line under the checks hid behind the sheet.
  const checklistBottom =
    sheetSnap === 0
      ? `calc(${COLLAPSED_SHEET_CSS} + 0.5rem)`
      : `calc(${SHEET_SNAPS[sheetSnap] * 100}dvh + env(safe-area-inset-bottom) + 0.75rem)`;

  return (
    <div className="fixed inset-0 overflow-hidden bg-ink">
      {/*
        Full-screen `cover`, which is only safe because the capture is 3:4.
        A 3:4 frame in a 9:19.5 viewport is fitted by height, so the crop falls
        entirely on the sides: the vertical field of view — head to knees, the
        axis every metric and gate check depends on — survives intact, and what
        is cut is mostly empty room. The model still analyses the full frame, so
        the measured area is wider than the shown one, never narrower.

        This is not the earlier mistake of *requesting* 9:16, which cropped the
        sensor itself and cost real field of view.
      */}
      {/* The front camera reads as a mirror, like any selfie view. Picture and
          skeleton flip together, so they stay aligned; the analysis is not flipped. */}
      <div
        className="absolute inset-0"
        style={{ transform: capture.facing === 'user' ? 'scaleX(-1)' : undefined }}
      >
        <video
          ref={capture.videoRef}
          playsInline
          muted
          className={`absolute inset-0 size-full object-cover transition-opacity duration-200 ${capture.previewReady ? 'opacity-100' : 'opacity-0'}`}
          aria-label="Camera preview"
        />
        <SkeletonOverlay
          landmarks={tick?.landmarks ?? []}
          videoRef={capture.videoRef}
          side={tick?.gate.side}
          fit="cover"
        />
      </div>

      {/* Top bar: minimal, legible over any background. */}
      <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 bg-gradient-to-b from-ink/70 to-transparent px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-8 text-white">
        <div className="flex items-start gap-2">
          <button
            onClick={() => navigate('/progress')}
            aria-label="Close setup"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-white/15 backdrop-blur"
          >
            <Icon name="close" size={16} strokeWidth={2.2} />
          </button>
          <div>
            <p className="text-sm font-semibold">Set up the shot</p>
            <p className="text-xs opacity-75">Side-on · head to knees in frame · {cameraDistance()}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/guide" className="text-xs font-semibold underline opacity-90">
            Guide
          </Link>
          <CameraFlipButton
            facing={capture.facing}
            onFlip={() => void capture.flipCamera()}
            disabled={!capture.streamReady}
          />
        </div>
      </div>

      {/*
        Live status, readable from across the room. Hidden while the sheet is
        expanded — the checklist is right there, and the chip would sit under it.
      */}
      <div
        className={`pointer-events-none absolute inset-x-0 top-24 z-10 flex flex-col items-center gap-2 px-4 text-center transition-opacity ${
          sheetExpanded && !autoStart.armed ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {autoStart.armed && !autoStart.audioReady ? (
          <button
            type="button"
            onClick={() => void arm()}
            className="pointer-events-auto rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-lg"
          >
            Tap to turn on sound
          </button>
        ) : null}
        {autoStart.armed ? (
          <ArmedIndicator
            progress={autoStart.progress}
            secondsLeft={autoStart.secondsLeft}
            allPassed={allPassed}
            failing={failing.map((c) => c.label)}
          />
        ) : null}
      </div>

      {capture.error ? (
        <div className="absolute inset-x-4 top-40 z-20 rounded-2xl bg-white p-4 text-sm shadow-lg">
          <p className="font-semibold">
            {capture.error.code === 'camera_denied' ? 'Camera blocked' : 'Camera problem'}
          </p>
          <p className="mt-1 text-ink-soft">{capture.error.message}</p>
          <Button variant="secondary" className="mt-3" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      ) : null}


      {/*
        Conditions live over the camera, not inside the sheet: you are looking
        at the picture while you move the phone, and having to open a panel to
        see why it is unhappy defeats the purpose. It rides just above whatever
        height the sheet is at, and object-cover leaves letterbox space here
        on a 9:16 screen, so the full list fits without covering the subject.
      */}
      <div
        className="pointer-events-none absolute inset-x-0 z-10 px-3"
        style={{ bottom: checklistBottom }}
      >
        <GateChecklist
          checks={checks.map((c) => ({ id: c.id, label: c.label, passed: c.passed }))}
          reasons={tick?.gate.reasons ?? []}
          loading={!capture.modelReady}
        />
      </div>

      <BottomSheet
        snaps={SHEET_SNAPS}
        snap={sheetSnap}
        onSnapChange={(index) => {
          setSheetSnap(index);
          setSheetExpanded(index === 2);
        }}
        header={
          <span className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold">
              {formatLength(plannedMin)} · {checks.filter((c) => c.passed).length}/{checks.length} checks
              {autoStart.armed ? ' · starts when you sit down' : ''}
            </span>
            <span className="text-xs text-muted">{sheetExpanded ? 'Hide' : 'Settings'}</span>
          </span>
        }
      >
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 rounded-full bg-surface p-0.5 text-xs font-semibold" role="tablist" aria-label="Session type">
            <span role="tab" aria-selected className="rounded-full bg-ink py-1.5 text-center text-white">
              Desk session
            </span>
            <Link role="tab" aria-selected={false} to="/check" replace className="rounded-full py-1.5 text-center text-ink-soft">
              Posture check
            </Link>
          </div>
          <div className="grid gap-2">
            {autoStart.armed ? (
              <Button variant="secondary" className="w-full" onClick={cancelAutoStart}>
                Cancel auto start
              </Button>
            ) : (
              <Button className="w-full" disabled={!capture.modelReady} onClick={armByHand}>
                Start when I sit down
              </Button>
            )}
            <Button
              variant="secondary"
              className="w-full"
              disabled={!allPassed}
              onClick={launch}
            >
              Start now
            </Button>
            <p className="text-xs text-muted">
              {autoStart.armed
                ? 'Put the phone down and go and sit. It chimes when it can see you properly and starts on its own — and nags if it still cannot.'
                : 'Start now needs every check above to pass first.'}
            </p>
          </div>

          <div className="border-t border-line pt-3">
            <p className="mb-2 text-sm font-medium">Session length</p>
            <div className="grid grid-cols-2 gap-1.5">
              {SESSION_LENGTH_CHOICES_MIN.map((minutes) => (
                <Pill
                  key={minutes}
                  active={plannedMin === minutes}
                  onClick={() => setPlanned(minutes as SessionLengthMin)}
                  className="w-full px-0"
                >
                  {formatLength(minutes)}
                </Pill>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted">
              15 minutes is enough for your weekly check. Have time? 30 minutes shows more: posture usually holds
              for a while, then starts to drift, and a longer check catches when that happens.
            </p>
          </div>

          <div className="border-t border-line pt-3">
            <Toggle
              checked={session.cues}
              onChange={(value) => {
                configure({ cues: value });
                setCuesEnabled(value);
              }}
              label="Sound cues"
              description="Chimes when the shot is good, when recording starts, and if it goes wrong."
            />
            {autoStart.armed && !autoStart.audioReady ? (
              <p className="mt-2 text-xs text-[color:var(--color-state-fhp)]">
                This browser would not let the app play sound. Watch the ring on screen instead.
              </p>
            ) : null}
          </div>

          <div className="border-t border-line pt-3">
            <p className="mb-1 text-sm font-medium">Personal baseline</p>
            <p className="mb-2 text-xs text-muted">
              {userDoc?.calibration?.cvaBaseline
                ? `Neck angle ${userDoc.calibration.cvaBaseline.toFixed(1)}° sitting tall, from your posture check on ${new Date(userDoc.calibration.measuredAt).toLocaleDateString()}. Your head counts as forward once it drops ${THRESHOLDS.cva.personalDropBad}° below this, or below ${THRESHOLDS.cva.good}° — whichever is stricter.`
                : 'Not measured yet. A one-minute posture check sets it, and desk sessions then compare you with yourself.'}
            </p>
            <Link to="/check" replace className="text-sm font-semibold underline">
              {userDoc?.calibration?.cvaBaseline ? 'Redo posture check' : 'Do a posture check'}
            </Link>
          </div>

          <p className="pb-2 text-xs text-muted">
            {capture.facing === 'environment' ? 'Rear' : 'Front'} camera · reads your posture {sampleFps}{' '}
            times a second
          </p>
        </div>
      </BottomSheet>
      <CameraLoading ready={capture.previewReady || Boolean(capture.error)} onClose={() => navigate('/progress')} />
    </div>
  );
}

/** "15 min", "1 hr" — short enough that all five lengths sit on one row. */
export function formatLength(minutes: number): string {
  return minutes < 60 ? `${minutes} min` : `${minutes / 60} hr`;
}

